import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { getWalletMemberAppointments, markAppointmentsCompleted } from '../../Services/APIs/patient';
import MemberFilter from './components/MemberFilter';
import SummaryBar from './components/SummaryBar';
import BookingCard from './components/BookingCard';
import CancelModal from './components/CancelModal';
import RescheduleModal from './components/RescheduleModal';
import RebookModal from './components/RebookModal';
import ViewDetailsModal from './components/ViewDetailsModal';

const formatDate = (isoString) => {
    try {
        return new Date(isoString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    } catch {
        return isoString;
    }
};

const parseAppointmentDateTime = (isoDate, timeStr) => {
    try {
        const date = new Date(isoDate);
        if (!timeStr) return date;
        const [timePart, period] = timeStr.trim().split(' ');
        let [hours, minutes] = timePart.split(':').map(Number);
        if (period === 'PM' && hours !== 12) hours += 12;
        if (period === 'AM' && hours === 12) hours = 0;
        date.setHours(hours, minutes, 0, 0);
        return date;
    } catch {
        return new Date(isoDate);
    }
};

const resolveStatus = (appt) => {
    if (appt.is_cancelled) return 'Cancelled';
    if (appt.is_completed) return 'Completed';
    const apptDateTime = parseAppointmentDateTime(appt.appointment_date, appt.appointment_time);
    if (apptDateTime < new Date(Date.now() - 10 * 60 * 1000)) return 'Completed';
    if (appt.is_rescheduled) return 'Rescheduled';
    return 'Scheduled';
};

const mapAppointment = (appt) => ({
    id: appt._id,
    clinicId: appt.clinic?._id || appt.clinic?.id || '',
    clinicName: appt.clinic?.name || 'Unknown Clinic',
    // `clinic_address` is the street field the clinic records actually carry; the legacy
    // `address` field is empty on every clinic, which is why this line rendered blank — and
    // shipped an empty address into the rebook confirmation email via RebookModal.
    clinicAddress: appt.clinic?.clinic_address || appt.clinic?.address || '',
    clinicAddress2: appt.clinic?.locality || appt.clinic?.address_2 || '',
    clinicState: appt.clinic?.state || '',
    clinicPincode: appt.clinic?.pincode || '',
    clinicSpecialty: 'General Dentistry',
    clinicDoctor: appt.clinic?.network_partner?.name || appt.clinic?.doctor || '',
    doctor: appt.clinic?.doctor_name || appt.clinic?.doctor || '',
    doctorExperienceYears: appt.clinic?.experience_years || appt.clinic?.network_partner?.experience_years || '',
    doctorPhone: appt.clinic?.contact_number || appt.clinic?.network_partner?.phone || '',
    doctorEmail: appt.clinic?.email || appt.clinic?.network_partner?.email || '',
    city: appt.clinic?.city || '',
    status: resolveStatus(appt),
    memberId: appt.beneficiary?.uid || appt.uid,
    memberName: appt.beneficiary?.name || 'Member',
    memberPhone: appt.beneficiary?.phone || appt.phone || '',
    date: formatDate(appt.appointment_date),
    time: appt.appointment_time,
    rawDate: appt.appointment_date,
    working_hours: appt.clinic?.clinic_timing || '',
    sunday_hours: appt.clinic?.sunday_working_hours || '',
});

const MyBookings = () => {
    const navigate = useNavigate();

    const [bookings, setBookings] = useState([]);
    const [members, setMembers] = useState([{ id: 'all', name: 'All members' }]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedMember, setSelectedMember] = useState('all');
    const [selectedStatus, setSelectedStatus] = useState('All');
    const [cancelBooking, setCancelBooking] = useState(null);
    const [rescheduleBooking, setRescheduleBooking] = useState(null);
    const [rebookBooking, setRebookBooking] = useState(null);
    const [viewDetailsBooking, setViewDetailsBooking] = useState(null);
    const [email] = useState(() => localStorage.getItem('insuranceLoginEmail') || '');
    const [companyName] = useState(() => localStorage.getItem('insuranceCompany') || '');

    const fetchAppointments = useCallback(async () => {
        if (!email) {
            setError('Session expired. Please login again.');
            setIsLoading(false);
            return;
        }
        try {
            const response = await getWalletMemberAppointments(email);
            const raw = response.data?.data || [];

            const tenMinsAgo = new Date(Date.now() - 10 * 60 * 1000);
            const toMark = raw
                .filter(a => !a.is_cancelled && !a.is_completed &&
                    parseAppointmentDateTime(a.appointment_date, a.appointment_time) < tenMinsAgo)
                .map(a => a._id);
            if (toMark.length > 0) {
                markAppointmentsCompleted(toMark).catch(() => {});
            }

            const mapped = raw.map(mapAppointment);
            setBookings(mapped);

            // Build unique member list from the returned appointments
            const seen = new Set();
            const uniqueMembers = [{ id: 'all', name: 'All members' }];
            mapped.forEach((b) => {
                if (!seen.has(b.memberId)) {
                    seen.add(b.memberId);
                    uniqueMembers.push({ id: b.memberId, name: b.memberName });
                }
            });
            setMembers(uniqueMembers);
        } catch (err) {
            if (err?.response?.status === 404) {
                // No wallet found — treat as empty, not an error
                setBookings([]);
            } else {
                setError('Failed to load appointments. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    }, [email]);

    useEffect(() => {
        fetchAppointments();
    }, [fetchAppointments]);

    // Member filter drives SummaryBar counts
    const memberFiltered = useMemo(() => {
        if (selectedMember === 'all') return bookings;
        return bookings.filter((b) => b.memberId === selectedMember);
    }, [bookings, selectedMember]);

    // Status pill further filters the card list
    const statusFiltered = useMemo(() => {
        if (selectedStatus === 'All') return memberFiltered;
        if (selectedStatus === 'Upcoming') return memberFiltered.filter((b) => b.status === 'Scheduled' || b.status === 'Rescheduled');
        return memberFiltered.filter((b) => b.status === selectedStatus);
    }, [memberFiltered, selectedStatus]);

    const upcomingCards = statusFiltered
        .filter((b) => b.status === 'Scheduled' || b.status === 'Rescheduled')
        .sort((a, b) => new Date(a.rawDate) - new Date(b.rawDate));
    const pastCards = statusFiltered
        .filter((b) => b.status === 'Completed')
        .sort((a, b) => new Date(b.rawDate) - new Date(a.rawDate));
    const cancelledCards = statusFiltered
        .filter((b) => b.status === 'Cancelled')
        .sort((a, b) => new Date(b.rawDate) - new Date(a.rawDate));
    const isEmpty = upcomingCards.length === 0 && pastCards.length === 0 && cancelledCards.length === 0;

    const handleCancel = (booking) => setCancelBooking(booking);
    const handleReschedule = (booking) => setRescheduleBooking(booking);

    const handleCancelSuccess = () => {
        setBookings((prev) =>
            prev.map((b) => b.id === cancelBooking.id ? { ...b, status: 'Cancelled' } : b)
        );
        setCancelBooking(null);
    };

    const handleRescheduleSuccess = ({ date, time }) => {
        setBookings((prev) =>
            prev.map((b) => b.id === rescheduleBooking.id ? { ...b, date, time, status: 'Rescheduled' } : b)
        );
        setRescheduleBooking(null);
    };

    const handleViewDetails = (booking) => setViewDetailsBooking(booking);

    const handleRebook = (booking) => setRebookBooking(booking);

    const handleRebookSuccess = () => {
        setRebookBooking(null);
        fetchAppointments();
    };

    return (
        <div className="min-h-screen bg-gray-200 flex flex-col">

            {/* Header */}
            <div className="bg-indigo-700 px-5 py-4 flex items-center gap-3">
                <button
                    onClick={() => navigate('/')}
                    className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all flex-shrink-0"
                    aria-label="Back to home"
                >
                    <ArrowLeft className="w-4 h-4 text-white" />
                </button>
                <h2 className="text-white font-bold text-lg">My Bookings</h2>
            </div>

            {/* Content */}
            <div className="flex-1 w-full max-w-lg mx-auto p-4">

                {/* Loading */}
                {isLoading && (
                    <div className="flex flex-col items-center justify-center py-24">
                        <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-4" />
                        <p className="text-gray-500 text-sm">Loading your appointments…</p>
                    </div>
                )}

                {/* Error */}
                {!isLoading && error && (
                    <div className="mt-6 bg-red-50 border border-red-100 rounded-2xl p-5 text-center">
                        <p className="text-red-600 font-medium mb-3">{error}</p>
                        <button
                            onClick={() => navigate('/')}
                            className="text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                        >
                            Go back to home
                        </button>
                    </div>
                )}

                {/* Main content — only when loaded and no error */}
                {!isLoading && !error && (
                    <>
                        <MemberFilter
                            members={members}
                            selectedMember={selectedMember}
                            onSelect={setSelectedMember}
                        />

                        <SummaryBar bookings={memberFiltered} selected={selectedStatus} onSelect={setSelectedStatus} />

                        {upcomingCards.length > 0 && (
                            <section>
                                <p className="text-xs font-bold text-gray-400 tracking-widest uppercase mb-3">Upcoming</p>
                                {upcomingCards.map((b) => (
                                    <BookingCard
                                        key={b.id}
                                        booking={b}
                                        onCancel={handleCancel}
                                        onReschedule={handleReschedule}
                                    />
                                ))}
                            </section>
                        )}

                        {pastCards.length > 0 && (
                            <section className={upcomingCards.length > 0 ? 'mt-2' : ''}>
                                <p className="text-xs font-bold text-gray-400 tracking-widest uppercase mb-3">Past</p>
                                {pastCards.map((b) => (
                                    <BookingCard
                                        key={b.id}
                                        booking={b}
                                        onViewDetails={handleViewDetails}
                                        onRebook={handleRebook}
                                    />
                                ))}
                            </section>
                        )}

                        {cancelledCards.length > 0 && (
                            <section className={(upcomingCards.length > 0 || pastCards.length > 0) ? 'mt-2' : ''}>
                                <p className="text-xs font-bold text-gray-400 tracking-widest uppercase mb-3">Cancelled</p>
                                {cancelledCards.map((b) => (
                                    <BookingCard key={b.id} booking={b} />
                                ))}
                            </section>
                        )}

                        {isEmpty && (
                            <div className="text-center py-16">
                                <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-8 h-8 text-gray-400"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth={1.5}
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                        />
                                    </svg>
                                </div>
                                <p className="text-gray-500 font-medium">No bookings found</p>
                                <p className="text-gray-400 text-sm mt-1">Try adjusting the filters above</p>
                            </div>
                        )}
                    </>
                )}
            </div>

            {cancelBooking && (
                <CancelModal
                    booking={cancelBooking}
                    email={email}
                    onClose={() => setCancelBooking(null)}
                    onSuccess={handleCancelSuccess}
                />
            )}

            {viewDetailsBooking && (
                <ViewDetailsModal
                    booking={viewDetailsBooking}
                    onClose={() => setViewDetailsBooking(null)}
                />
            )}

            {rescheduleBooking && (
                <RescheduleModal
                    booking={rescheduleBooking}
                    email={email}
                    onClose={() => setRescheduleBooking(null)}
                    onSuccess={handleRescheduleSuccess}
                />
            )}

            {rebookBooking && (
                <RebookModal
                    booking={rebookBooking}
                    email={email}
                    companyName={companyName}
                    onClose={() => setRebookBooking(null)}
                    onSuccess={handleRebookSuccess}
                />
            )}
        </div>
    );
};

export default MyBookings;
